<script setup lang="ts" generic="T extends AcceptableValue">
import { mdiCheck } from "@mdi/js";
import {
	injectSelectRootContext,
	SelectItem,
	SelectItemIndicator,
	SelectItemText,
	type AcceptableValue,
	type SelectItemSelectEvent,
} from "reka-ui";
import { computed } from "vue";
import { Icon } from "../icon";

const props = defineProps<{
	value: T;
	textValue?: string;
	disabled?: boolean;
}>();

const emit = defineEmits<{
	select: [data: T];
}>();

const EMPTY_TOKEN = "__EMPTY__";

const rootCtx = injectSelectRootContext();

// Reka-UI don't allow empty string, so we hack it.
// Ref: https://github.com/radix-ui/primitives/issues/2706
function handleSelect(event: SelectItemSelectEvent<AcceptableValue>) {
	if (event.detail.value === EMPTY_TOKEN) {
		event.preventDefault();
		if (props.disabled) {
			return;
		}

		rootCtx.onValueChange("");

		if (!rootCtx.multiple.value) {
			rootCtx.onOpenChange(false);
		}
	}

	if (event.detail.value != null) {
		emit("select", event.detail.value as any);
	}
}

const isSelected = computed(() => {
	if (Array.isArray(rootCtx.modelValue.value)) {
		return rootCtx.modelValue.value.some((v) => v === props.value);
	} else {
		return rootCtx.modelValue.value === props.value;
	}
});
</script>

<template>
	<SelectItem class="select-option" v-bind="props" @select="handleSelect">
		<slot name="indicator" :selected="isSelected">
			<SelectItemIndicator asChild>
				<span class="absolute right-2 flex items-center justify-center">
					<Icon size="sm" :d="mdiCheck" />
				</span>
			</SelectItemIndicator>
		</slot>

		<SelectItemText>
			<slot />
		</SelectItemText>
	</SelectItem>
</template>
